import type { Building } from "building-types/building/buildingTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { WallType } from "building-types/walls/wallEnums";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { wallLateralDatumAnchorToWallLayer } from "doc/elements/wallLateralDatumAnchorToWallLayer";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { getWindowHomeSettings } from "doc/elements/window/windowHomeSettings";
import { getMergeSettingsForOpeningParamsFromBuilding } from "doc/mergeSettingsSet";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLength } from "geom/planarGraph";
import { wallThickness } from "../elements/walls/wall";
import { solidWallSubstrateLayer } from "../elements/walls/wallLayers";
import { makeWindowUnit } from "../elements/window/windowUnit";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

function select({
  settings,
  planarGraphs,
  planarGraphPerimeters,
  windowPlacements: windows,
  windowUnits,
  wallMasses,
  mirror,
  settingsSets,
  roomOutlineTable,
  optionChoices,
  doc,
}: Pick<
  Building,
  | "settings"
  | "planarGraphs"
  | "planarGraphPerimeters"
  | "windowPlacements"
  | "windowUnits"
  | "wallMasses"
  | "mirror"
  | "settingsSets"
  | "roomOutlineTable"
  | "optionChoices"
  | "doc"
>) {
  return {
    windows,
    windowUnitsFromBuilding: windowUnits,
    planarGraphs,
    planarGraphPerimeters,
    wallMasses,
    roomOutlineTable,
    wallSettings: settings.wall,
    propCatalog: settings.props,
    assets: doc.assets,
    wainscotingSettings: settings.wainscoting,
    mirror,
    mergeSettingsForOpeningParams: getMergeSettingsForOpeningParamsFromBuilding(
      { settingsSets, optionChoices, settings },
      doc,
    ),
    windowHomeSettings: getWindowHomeSettings(settings),
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  mergeSettingsForOpeningParams: "deep",
  windowHomeSettings: "shallow",
});

async function generate({
  windows,
  windowUnitsFromBuilding,
  planarGraphs,
  planarGraphPerimeters,
  wallMasses,
  roomOutlineTable,
  wallSettings,
  propCatalog,
  assets,
  wainscotingSettings,
  mirror,
  windowHomeSettings,
  mergeSettingsForOpeningParams,
}: ReturnType<typeof select>): Promise<{ windowUnits: Table<WindowUnit>; issues: BuildingGenerationIssue[] }> {
  const windowUnits = { ...windowUnitsFromBuilding };
  const issues: BuildingGenerationIssue[] = [];

  for (const windowId in windows) {
    const window = windows[windowId];
    if (!window.opening || (!window.frontRoom && !window.backRoom)) {
      continue;
    }
    const levelId = window.frontRoom?.level.id ?? window.backRoom?.level.id;
    assertIsNotUndefined(levelId);
    const wallMassSystem = wallMasses[levelId];
    const wallIndex = wallMassSystem.wallIndex;
    const wall = wallIndex.walls[wallMassSystem.halfEdgeToWall[window.halfEdgeIndex]];

    if (wall.wallType === WallType.VOID) {
      continue;
    }

    const planarGraph = planarGraphs[levelId].planarGraph;
    const wallLength = planarGraphHalfEdgeLength(planarGraph, window.halfEdgeIndex);
    const wallMass = solidWallSubstrateLayer(wall);
    const wallDatum = window.lateralDatum ?? WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE;
    const wallAlignmentLayer = wallLateralDatumAnchorToWallLayer[wallDatum];

    const datumAlignmentOffset = wallThickness({ wall, wallLayer: wallAlignmentLayer });

    const oppHalfEdge = planarGraphAdjacentHalfEdge(window.halfEdgeIndex);
    const streetOrientation = planarGraphPerimeters[levelId].coveredPerimeterStreetOrientations[oppHalfEdge];

    // get outermost exterior material
    let exteriorWallMaterial: ExteriorMaterial | undefined;

    const { entitySettings, activeSettingsSets, settings, optionChoices } = mergeSettingsForOpeningParams;

    const mergedWindowSettings = getMergedWindowSettings({
      homeSettings: windowHomeSettings,
      window,
      descriptor: window.descriptor,
      entitySettings,
      activeSettingsSets,
      settings,
      optionChoices,
    });

    const entityHandleForSelectingReportingUnit =
      roomEntityHandleForSelectingReportingUnit(window.frontRoom) ??
      roomEntityHandleForSelectingReportingUnit(window.backRoom);

    const mirrorForUnit = isCombinedGlobalInstanceMirror(mirror, window.frontRoom ?? window.backRoom);

    const unit = await makeWindowUnit({
      window,
      issues,
      wallLength: wallLength,
      wallThickness: wallMass.thickness,
      wallSettings,
      exteriorWallMaterial,
      propCatalog,
      assets,
      wainscotingSettings,
      datumAlignmentOffset,
      streetOrientation,
      mirror: mirrorForUnit,
      wallMassSystem,
      roomOutlines: roomOutlineTable[ensureDefined(window.frontRoom).id],
      mergedWindowSettings,
      context: {
        isFront: true,
        isLeft: true,
      },
      entityHandleForSelectingReportingUnit,
    });

    if (unit) {
      windowUnits[windowId] = unit;
    } else {
      issues.push({
        type: BuildingGenerationIssueType.INVALID_WINDOW_SASH_GEOMETRY,
        handle: window.handle,
      });
    }
  }

  return { windowUnits, issues };
}

export default memoAsync(select, generate, compare);
